import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CategoryModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  category = null, 
  categories = [],
  mode = 'create'
}) => {
  const [formData, setFormData] = useState({
    name: '',
    parentCategory: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes or category changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && category) {
        setFormData({
          name: category?.name || '',
          parentCategory: category?.parentId || '',
          description: category?.description || ''
        });
      } else {
        setFormData({
          name: '',
          parentCategory: '',
          description: ''
        });
      }
      setErrors({});
    }
  }, [isOpen, category, mode]);

  // Check if a category is a descendant of another
 const isDescendant = (potentialDescendant, ancestor) => {
    if (potentialDescendant?.parentId === ancestor?.id) return true;
    const parent = categories?.find(cat => cat?.id === potentialDescendant?.parentId);
    return parent ? isDescendant(parent, ancestor) : false;
  };

  // Generate parent category options
    const parentCategoryOptions = [
    { value: '', label: 'None (Root Category)' },
    ...categories?.filter(cat => {
        // Exclude current category and its descendants when editing
        if (mode === 'edit' && category) {
          return cat?.id !== category?.id && !isDescendant(cat, category);
        }
        return true;
      })?.map(cat => ({
        value: cat?.id,
        label: `${'  '?.repeat(cat?.level - 1)}${cat?.name} (Level ${cat?.level})`,
        disabled: cat?.level >= 3
      }))
  ];

  
  

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Category name validation
    if (!formData?.name?.trim()) {
      newErrors.name = 'Category name is required';
    } else if (formData?.name?.trim()?.length < 2) {
      newErrors.name = 'Category name must be at least 2 characters';
    } else if (formData?.name?.trim()?.length > 50) {
      newErrors.name = 'Category name must be less than 50 characters';
    }

    // Check for duplicate names at the same level
    const existingCategory = categories?.find(cat => 
      cat?.name?.toLowerCase() === formData?.name?.trim()?.toLowerCase() &&
      cat?.parentId === formData?.parentCategory &&
      (mode === 'create' || cat?.id !== category?.id)
    );
    
    if (existingCategory) {
      newErrors.name = 'A category with this name already exists at this level';
    }

    // Description validation
    if (formData?.description && formData?.description?.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    // Parent category validation (prevent circular references)
    if (mode === 'edit' && category && formData?.parentCategory) {
      const selectedParent = categories?.find(cat => cat?.id === formData?.parentCategory);
      if (selectedParent && isDescendant(selectedParent, category)) {
        newErrors.parentCategory = 'Cannot set a descendant category as parent';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      const submitData = {
        ...formData,
        name: formData?.name?.trim(),
        description: formData?.description?.trim()
      };

      if (mode === 'edit') {
        submitData.id = category?.id;
      }

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Error submitting category:', error);
      setErrors({ submit: 'Failed to save category. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-90 transition-opacity"
        onClick={handleClose}
      />
      {/* Modal */}
      <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
        <div className="bg-card rounded-lg border border-border shadow-modal w-full max-w-md max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {mode === 'edit' ? 'Edit Category' : 'Create New Category'}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {mode === 'edit' ?'Update category information and hierarchy' :'Add a new category to your product catalog'
                }
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={handleClose}
              disabled={isSubmitting}
            />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Category Name */}
            <Input
              label="Category Name"
              type="text"
              placeholder="Enter category name"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              error={errors?.name}
              required
              maxLength={50}
              disabled={isSubmitting}
            />

            {/* Parent Category */}
            <Select
              label="Parent Category"
              placeholder="Select parent category"
              options={parentCategoryOptions}
              value={formData?.parentCategory}
              onChange={(value) => handleInputChange('parentCategory', value)}
              error={errors?.parentCategory}
              disabled={isSubmitting}
              description="Leave empty to create a root category"
            />

            {/* Description */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Description
                <span className="text-muted-foreground font-normal ml-1">(Optional)</span>
              </label>
              <textarea
                placeholder="Enter category description"
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                disabled={isSubmitting}
                maxLength={500}
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
              />
              <div className="flex justify-between items-center">
                {errors?.description && (
                  <p className="text-sm text-destructive">{errors?.description}</p>
                )}
                <p className="text-xs text-muted-foreground ml-auto">
                  {formData?.description?.length}/500 characters
                </p>
              </div>
            </div>

            {/* Submit Error */}
            {errors?.submit && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm text-destructive">{errors?.submit}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isSubmitting}
                iconName={mode === 'edit' ? 'Save' : 'Plus'}
                iconPosition="left"
              >
                {mode === 'edit' ? 'Update Category' : 'Create Category'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CategoryModal;